import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button } from '@mono/ui-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useShredView } from '../../../hooks/useShredView';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
import { exportRequirements } from '../../../lib/utils/exportToExcel';
import { RequirementCategory } from '../../../types/proposal';
import { ComplianceDetailDrawer } from '../ComplianceMatrix/ComplianceDetailDrawer';
import { ExportButton } from '../ComplianceMatrix/ExportButton';
import { RequirementCard } from './RequirementCard';
import { RequirementFilters } from './RequirementFilters';
/** Category metadata for display */ const CATEGORY_INFO = {
    [RequirementCategory.PROPOSAL_INSTRUCTION]: {
        label: 'Proposal Instructions',
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        activeColor: 'bg-blue-100 text-blue-800 border-blue-400'
    },
    [RequirementCategory.FORMAT_REQUIREMENT]: {
        label: 'Format',
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        activeColor: 'bg-blue-100 text-blue-800 border-blue-400'
    },
    [RequirementCategory.SUBMISSION_REQUIREMENT]: {
        label: 'Submission',
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        activeColor: 'bg-blue-100 text-blue-800 border-blue-400'
    },
    [RequirementCategory.SCOPE_REQUIREMENT]: {
        label: 'Scope',
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        activeColor: 'bg-purple-100 text-purple-800 border-purple-400'
    },
    [RequirementCategory.PERFORMANCE_STANDARD]: {
        label: 'Performance',
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        activeColor: 'bg-purple-100 text-purple-800 border-purple-400'
    },
    [RequirementCategory.DELIVERABLE]: {
        label: 'Deliverable',
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        activeColor: 'bg-purple-100 text-purple-800 border-purple-400'
    },
    [RequirementCategory.TASK_REQUIREMENT]: {
        label: 'Task',
        color: 'bg-purple-50 text-purple-700 border-purple-200',
        activeColor: 'bg-purple-100 text-purple-800 border-purple-400'
    },
    [RequirementCategory.EVALUATION_CRITERION]: {
        label: 'Evaluation',
        color: 'bg-green-50 text-green-700 border-green-200',
        activeColor: 'bg-green-100 text-green-800 border-green-400'
    },
    [RequirementCategory.QUALIFICATION]: {
        label: 'Qualification',
        color: 'bg-green-50 text-green-700 border-green-200',
        activeColor: 'bg-green-100 text-green-800 border-green-400'
    },
    [RequirementCategory.COMPLIANCE_REQUIREMENT]: {
        label: 'Compliance',
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        activeColor: 'bg-amber-100 text-amber-800 border-amber-400'
    },
    [RequirementCategory.CLAUSE_FLOWDOWN]: {
        label: 'Clause Flowdown',
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        activeColor: 'bg-amber-100 text-amber-800 border-amber-400'
    },
    [RequirementCategory.SECURITY_REQUIREMENT]: {
        label: 'Security',
        color: 'bg-amber-50 text-amber-700 border-amber-200',
        activeColor: 'bg-amber-100 text-amber-800 border-amber-400'
    },
    [RequirementCategory.KEY_PERSONNEL]: {
        label: 'Key Personnel',
        color: 'bg-teal-50 text-teal-700 border-teal-200',
        activeColor: 'bg-teal-100 text-teal-800 border-teal-400'
    },
    [RequirementCategory.PERIOD_OF_PERFORMANCE]: {
        label: 'Period of Performance',
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        activeColor: 'bg-indigo-100 text-indigo-800 border-indigo-400'
    },
    [RequirementCategory.PLACE_OF_PERFORMANCE]: {
        label: 'Place of Performance',
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        activeColor: 'bg-indigo-100 text-indigo-800 border-indigo-400'
    },
    [RequirementCategory.REPORTING_REQUIREMENT]: {
        label: 'Reporting',
        color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        activeColor: 'bg-indigo-100 text-indigo-800 border-indigo-400'
    },
    [RequirementCategory.CLIN]: {
        label: 'CLIN',
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        activeColor: 'bg-gray-100 text-gray-800 border-gray-400'
    },
    [RequirementCategory.OTHER]: {
        label: 'Other',
        color: 'bg-gray-50 text-gray-700 border-gray-200',
        activeColor: 'bg-gray-100 text-gray-800 border-gray-400'
    }
};
/** Order of categories in the filter chips */ const CATEGORY_ORDER = Object.values(RequirementCategory);
/**
 * Main container for shred view with category filter chips.
 * Replaces the old 3-tab layout with a unified view that supports
 * filtering by any of the 18 requirement categories.
 */ export const ShredViewContainer = /*#__PURE__*/ memo(({ projectId })=>{
    const [selectedCategory, setSelectedCategory] = useState(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedObligations, setSelectedObligations] = useState([]);
    const [statusFilter, setStatusFilter] = useState([]);
    const [page, setPage] = useState(1);
    const [selectedRequirement, setSelectedRequirement] = useState(null);
    const limit = 20;
    // Debounce search to avoid excessive API calls on every keystroke
    useEffect(()=>{
        const timer = setTimeout(()=>{
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 300);
        return ()=>clearTimeout(timer);
    }, [
        searchTerm
    ]);
    const { data, isLoading } = useShredView(projectId, selectedCategory, {
        page,
        limit,
        search: debouncedSearch || undefined,
        obligationStrength: selectedObligations.length > 0 ? selectedObligations : undefined,
        status: statusFilter.length > 0 ? statusFilter : undefined
    });
    const filteredRequirements = data?.requirements ?? [];
    const handleCategoryToggle = (cat)=>{
        setSelectedCategory((prev)=>prev === cat ? undefined : cat);
        setPage(1);
    };
    const handleObligationToggle = (obligation)=>{
        setSelectedObligations((prev)=>prev.includes(obligation) ? prev.filter((o)=>o !== obligation) : [
                ...prev,
                obligation
            ]);
        setPage(1);
    };
    const handleStatusFilterChange = (status)=>{
        setStatusFilter((prev)=>prev.includes(status) ? prev.filter((s)=>s !== status) : [
                ...prev,
                status
            ]);
        setPage(1);
    };
    const handleExport = useCallback(async (format)=>{
        // Fetch all requirements (not just the current page)
        const allData = await proposalProjectApiConnect.getShredView(projectId, {
            limit: 10000,
            sortBy: 'page',
            sortOrder: 'asc'
        });
        exportRequirements(allData.requirements, format);
    }, [
        projectId
    ]);
    const byCategory = data?.stats?.byCategory ?? {};
    const totalPages = data?.pagination?.pages ?? 0;
    // Only show categories that have requirements
    const visibleCategories = useMemo(()=>CATEGORY_ORDER.filter((cat)=>byCategory[cat] > 0), [
        byCategory
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center py-12",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "text-center space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Loading requirements..."
                    })
                ]
            })
        });
    }
    const totalRequirements = Object.values(byCategory).reduce((sum, n)=>sum + n, 0);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-2xl font-bold text-gray-900",
                                children: "Shred View"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-gray-600",
                                children: "Requirements extracted from solicitation documents. Filter by category, obligation strength, or compliance status."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4",
                        children: [
                            /*#__PURE__*/ _jsx(ExportButton, {
                                onExport: handleExport,
                                disabled: totalRequirements === 0
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-right",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-2xl font-bold text-gray-900",
                                        children: totalRequirements
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-xs text-gray-500",
                                        children: "Total Requirements"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            visibleCategories.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium text-gray-700 uppercase tracking-wide",
                        children: "Category"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-2",
                        children: visibleCategories.map((cat)=>{
                            const info = CATEGORY_INFO[cat];
                            const isSelected = selectedCategory === cat;
                            const count = byCategory[cat] ?? 0;
                            return /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>handleCategoryToggle(cat),
                                children: /*#__PURE__*/ _jsxs(Badge, {
                                    variant: "outline",
                                    className: `text-xs font-medium border cursor-pointer transition-colors ${isSelected ? info?.activeColor ?? 'bg-gray-200 text-gray-800 border-gray-400' : info?.color ?? 'bg-gray-50 text-gray-600 border-gray-200'}`,
                                    children: [
                                        info?.label ?? cat,
                                        " (",
                                        count,
                                        ")"
                                    ]
                                })
                            }, cat);
                        })
                    })
                ]
            }),
            data && /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-medium text-gray-700",
                                children: "Addressed"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xl font-semibold text-green-600",
                                children: data.stats.byStatus?.ADDRESSED ?? 0
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-medium text-gray-700",
                                children: "In Progress"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xl font-semibold text-blue-600",
                                children: data.stats.byStatus?.IN_PROGRESS ?? 0
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-medium text-gray-700",
                                children: "Not Started"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xl font-semibold text-gray-600",
                                children: data.stats.byStatus?.NOT_STARTED ?? 0
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-medium text-gray-700",
                                children: "Mandatory"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xl font-semibold text-red-600",
                                children: data.stats.mandatory
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(RequirementFilters, {
                searchTerm: searchTerm,
                onSearchChange: setSearchTerm,
                selectedObligations: selectedObligations,
                onObligationToggle: handleObligationToggle,
                statusFilter: statusFilter,
                onStatusFilterChange: handleStatusFilterChange
            }),
            filteredRequirements.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                className: "text-center py-12",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-500",
                        children: "No requirements found."
                    }),
                    selectedCategory && /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>setSelectedCategory(undefined),
                        className: "text-sm text-blue-600 hover:text-blue-700 mt-2",
                        children: "Clear category filter"
                    })
                ]
            }) : /*#__PURE__*/ _jsx("div", {
                className: "space-y-3",
                children: filteredRequirements.map((requirement)=>/*#__PURE__*/ _jsx(RequirementCard, {
                        requirement: requirement,
                        onClick: setSelectedRequirement
                    }, requirement.requirementId))
            }),
            totalPages > 1 && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between pt-4 border-t",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm text-gray-600",
                        children: [
                            "Page ",
                            page,
                            " of ",
                            totalPages,
                            " (",
                            data?.stats?.total ?? 0,
                            " total requirements)"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "outline",
                                size: "sm",
                                onClick: ()=>setPage((p)=>Math.max(1, p - 1)),
                                disabled: page === 1,
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Previous"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                variant: "outline",
                                size: "sm",
                                onClick: ()=>setPage((p)=>Math.min(totalPages, p + 1)),
                                disabled: page === totalPages,
                                children: [
                                    "Next",
                                    /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "w-4 h-4"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            selectedRequirement && /*#__PURE__*/ _jsx(ComplianceDetailDrawer, {
                requirement: selectedRequirement,
                projectId: projectId,
                isOpen: !!selectedRequirement,
                onClose: ()=>setSelectedRequirement(null)
            })
        ]
    });
});
ShredViewContainer.displayName = 'ShredViewContainer';
